import React from 'react';
import { ToastContext, Dropdown, Modal, Button, Numbers, Hierarchy, Section } from '@weezevent/nacre';
import { useTranslation } from 'react-i18next';
import useAppContext from '../../../context/useAppContext';
import PropTypes from 'prop-types';
import SessionModel from '../../../models/sessionModel';
import i18n from 'i18next/';
import Styles from './ratesAction.module.css';

function HierarchyRates({ rates, selected, onSelected, locale, currency }) {
    const { t } = useTranslation();

    const treeRates = React.useMemo(() => {
        return rates.toOptions({
            extraRate: rate => ({
                component: (
                    <Numbers
                        className={Styles['price']}
                        value={rate.rate_template ? rate.rate_template.price : rate.price}
                        locale={locale}
                        currency={currency || 'EUR'}
                        showNullDecimal
                    />
                ),
                classname: 'row-rate'
            }),
            extraGroup: group => ({
                classname: group.parent ? 'row-sub-group' : 'row-group'
            })
        });
    }, [rates, locale, currency]);

    const _onSelected = React.useCallback(
        ({ event, data: { checked } }, node) => {
            event.stopPropagation();
            onSelected(currentState => {
                let newSelected = [...currentState].filter(value => value !== node.value);

                if (checked) {
                    newSelected.push(node.value);
                }

                return newSelected;
            });
        },
        [onSelected]
    );

    return (
        <div className={Styles['hierarchy']}>
            <Section>
                <Hierarchy
                    items={treeRates}
                    searchable
                    selected={selected}
                    onSelected={_onSelected}
                    selectable
                    selectableGroup={false}
                    withSelectAll={false}
                    placeholderSearch={t('ticket.common.action.search')}
                    height={'none'}
                    chevron
                    depth={0}
                    widthIndent={'14px'}
                />
            </Section>
        </div>
    );
}

export function RatesModal({ items = null, onSubmit = () => {}, onClose = () => {}, loading = false, selected = [], rates = null }) {
    let [{ event }] = useAppContext();
    const { t } = useTranslation();
    const locale = i18n.language;
    const [_selected, setSelected] = React.useState(selected);

    React.useEffect(() => {
        setSelected(selected);
    }, [selected]);

    const _onSubmit = React.useCallback(() => {
        onSubmit(_selected);
    }, [_selected, onSubmit]);

    if (!rates || !items || items.length === 0) {
        return null;
    }

    return (
        <Modal
            size={'large'}
            open={Boolean(items)}
            onClose={onClose}
            title={t('ticket.sessions.actions.rates.modalTitle')}
            submitButton={<Button primary disabled={loading} isLoading={loading} onClick={_onSubmit} label={t('ticket.common.action.save')} />}
            cancelButton={<Button disabled={loading} onClick={onClose} label={t('ticket.common.action.cancel')} />}
        >
            <HierarchyRates rates={rates} locale={locale} currency={event.currency || 'EUR'} selected={_selected} onSelected={setSelected} />
        </Modal>
    );
}

RatesModal.propTypes = {
    items: PropTypes.array,
    onSubmit: PropTypes.func,
    onClose: PropTypes.func,
    loading: PropTypes.bool,
    selected: PropTypes.array,
    rates: PropTypes.array
};

export function RatesAction({ objects: selectedSessions, rates = null, onSave = () => {}, loadingRates = false }) {
    const [itemsToAction, setItemsToAction] = React.useState([]);
    const toast = React.useContext(ToastContext);
    const { t } = useTranslation();
    const [{ event, organization }] = useAppContext();

    const [isSubmitting, setIsSubmitting] = React.useState(false);

    const itemsFiltered = React.useMemo(() => {
        if (!selectedSessions) {
            return [];
        }
        return selectedSessions;
    }, [selectedSessions]);

    const selectedRates = React.useMemo(() => {
        let _selectedRates = [];
        if (!selectedSessions || !selectedSessions.length || !itemsFiltered.length) {
            return [];
        }

        itemsFiltered.forEach(session => {
            session.rates.forEach(rate => {
                _selectedRates.push(Number(rate.rate_id));
            });
        });

        //Check unicity with Set
        return Array.from(new Set(_selectedRates));
    }, [selectedSessions, itemsFiltered]);

    const handleSubmit = React.useCallback(
        selected => {
            setIsSubmitting(true);
            SessionModel.perform_action('link_rates', itemsToAction, { organization, event, rate_ids: selected })
                .then(() => {
                    toast.success(t(`ticket.sessions.actions.rates.success`, { count: itemsFiltered.length }));
                    setItemsToAction(null);
                    onSave();
                })
                .catch(e => {
                    console.error(e);
                    toast.error(t(`ticket.sessions.actions.rates.error`, { count: itemsFiltered.length }));
                })
                .finally(() => {
                    setIsSubmitting(false);
                });
        },
        [event, organization, SessionModel, toast, itemsToAction, onSave, itemsFiltered]
    );

    if (!rates || loadingRates) {
        return null;
    }

    return (
        <>
            <RatesModal items={itemsToAction} rates={rates} onClose={() => setItemsToAction(null)} onSubmit={handleSubmit} loading={isSubmitting} selected={selectedRates} />
            <Dropdown.Item disabled={!itemsFiltered.length} onClick={() => setItemsToAction(itemsFiltered.length > 0 ? itemsFiltered : null)}>
                {t('ticket.sessions.actions.rates.label')}
            </Dropdown.Item>
        </>
    );
}

RatesAction.propTypes = {
    objects: PropTypes.array.isRequired, //required
    rates: PropTypes.array,
    onSave: PropTypes.func
};
